/**
 * Choice Modal Component
 *
 * A confirmation modal dialog that presents users with a choice between two actions.
 * Typically used for confirming destructive actions or important decisions.
 *
 * Features a success transition animation similar to the 2FA verification modal.
 */

import React, { useState } from 'react';
import { FiX, FiAlertTriangle, FiCheck, FiTrash2 } from 'react-icons/fi';
import '../styles/ChoiceModal.css';

/**
 * ChoiceModal - A confirmation dialog with confirm/cancel options and success transition
 *
 * @param {Object} props - Component props
 * @param {Object|null} props.notification - Notification object containing modal configuration
 * @param {string} [props.notification.title='Confirmation'] - Modal title
 * @param {string} props.notification.message - Main message to display
 * @param {React.ReactNode} [props.notification.icon] - Icon to display (defaults to warning triangle)
 * @param {string} [props.notification.confirmText='Confirm'] - Text for confirm button
 * @param {string} [props.notification.cancelText='Cancel'] - Text for cancel button
 * @param {boolean} [props.notification.isLoading=false] - Whether confirm button is in loading state
 * @param {string} [props.notification.loadingText] - Text to show when loading
 * @param {boolean} [props.notification.showSuccessTransition=false] - Whether to show success animation after confirm
 * @param {string} [props.notification.successTitle='Done!'] - Title shown during success transition
 * @param {string} [props.notification.successMessage='Action completed successfully.'] - Message shown during success transition
 * @param {Function} [props.notification.onConfirm] - Callback for confirm action
 * @param {Function} [props.notification.onCancel] - Callback for cancel action
 * @param {Function} props.onClose - Callback to close the modal
 * @returns {JSX.Element|null} ChoiceModal component or null if no notification
 */
const ChoiceModal = ({ notification, onClose }) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  if (!notification) return null;

  const handleSuccessTransition = async () => {
    try {
      // Execute onConfirm callback first (e.g., API delete call)
      // Wait for it to complete before showing success
      if (notification.onConfirm) {
        await notification.onConfirm();
      }

      // Show success state only if onConfirm didn't throw
      setShowSuccess(true);

      // Wait for success animation (matches 2FA timing)
      await new Promise(resolve => setTimeout(resolve, 1200));

      // Start closing animation
      setIsClosing(true);

      // Wait for close animation
      await new Promise(resolve => setTimeout(resolve, 400));

      // Close the modal
      onClose();
    } catch (err) {
      // If onConfirm throws, just close the modal without success animation
      // The error should have been handled by the onConfirm callback
      console.error('Action failed:', err);
      onClose();
    }
  };

  const handleConfirm = () => {
    if (notification.isLoading) return; // Prevent action when loading

    // If showSuccessTransition is enabled, show the success animation
    if (notification.showSuccessTransition) {
      handleSuccessTransition();
    } else {
      // Original behavior - immediately close and execute callback
      if (notification.onConfirm) {
        notification.onConfirm();
      }
      onClose();
    }
  };

  const handleCancel = () => {
    if (showSuccess) return; // Don't allow cancel during success transition
    if (notification.onCancel) {
      notification.onCancel();
    }
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !showSuccess) {
      handleCancel();
    }
  };

  // Determine if this is a delete/destructive action
  const isDestructive = notification.confirmText?.toLowerCase().includes('delete') ||
                        notification.title?.toLowerCase().includes('delete');

  return (
    <div
      className={`choice-modal-backdrop ${isClosing ? 'closing' : ''}`}
      onClick={handleBackdropClick}
    >
      <div className={`choice-modal ${isClosing ? 'closing' : ''}`}>
        {!showSuccess ? (
          <>
            <div className="choice-modal-header">
              <div className="choice-modal-icon">
                {notification.icon || <FiAlertTriangle />}
              </div>
              <div className="choice-modal-title-section">
                <h3 className="choice-modal-title">
                  {notification.title || 'Confirmation'}
                </h3>
              </div>
              <button
                className="choice-modal-close"
                onClick={handleCancel}
                aria-label="Close"
              >
                <FiX />
              </button>
            </div>

            <div className="choice-modal-body">
              <p className="choice-modal-message">
                {notification.message}
              </p>
            </div>

            <div className="choice-modal-actions">
              <button
                className="choice-modal-button cancel"
                onClick={handleCancel}
                disabled={notification.isLoading}
              >
                {notification.cancelText || 'Cancel'}
              </button>
              <button
                className="choice-modal-button confirm"
                onClick={handleConfirm}
                disabled={notification.isLoading}
              >
                {notification.isLoading ? (
                  <>
                    <div className="choice-modal-spinner"></div>
                    {notification.loadingText || 'Loading...'}
                  </>
                ) : (
                  notification.confirmText || 'Confirm'
                )}
              </button>
            </div>
          </>
        ) : (
          /* Success State */
          <div className="choice-modal-success-container">
            <div className={`choice-modal-success-icon ${isDestructive ? 'destructive' : ''}`}>
              {isDestructive ? <FiTrash2 /> : <FiCheck />}
            </div>
            <h2 className="choice-modal-success-title">
              {notification.successTitle || (isDestructive ? 'Deleted!' : 'Done!')}
            </h2>
            <p className="choice-modal-success-message">
              {notification.successMessage || (isDestructive ? 'Item has been permanently deleted.' : 'Action completed successfully.')}
            </p>
            <div className="choice-modal-success-loader">
              <div className="choice-modal-success-loader-bar"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChoiceModal;
